import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import * as ProgramDaysApi from 'api/programDays';
import { DATA_BAG } from 'defs/offlineAvailability';
import Sequences from 'defs/sequences';
import * as programSagas from 'sagas/programs';
import { actionCreators as connectionAC, getIsOnline } from 'store/connection';
import { actionTypes as loginActionTypes } from 'store/login';
import { getIsAuthenticated } from 'store/login';
import { actionCreators as offlineAvailabilityAC } from 'store/offlineAvailability';
import { ProgramDayMinType, ProgramDayType } from 'store/programDays';
import {
	actionCreators as programDaysAC,
	actionTypes as programDaysActionTypes,
	getCalendar,
	getCalendarToday,
	getCurrentDayNumber,
} from 'store/programDays';
import { actionTypes as programItemsActionTypes } from 'store/programItems';
import { actionTypes as programPreviewActionTypes } from 'store/programPreview';
import { getActiveProgramId } from 'store/programs';
import { actionCreators as sequenceAC, getSequenceIsRunning } from 'store/sequences';

/**
 * fetches todos lazy for performace reasons
 * @see PULSWM-2299
 */
export function* loadCalendarTodos(calendar: Array<ProgramDayType>, activeProgramId: number, todayDayNumber: number) {
	const offset: number = 4;
	if (calendar.length < 1) {
		return;
	}

	try {
		let i: number;
		// step back in steps (5 days) from todayDay
		for (i = todayDayNumber; i > 0; i -= offset + 1) {
			const dayNumber = i;
			const programDays = yield call(
				ProgramDaysApi.fetchCalendarProgramDayTodos as any,
				activeProgramId,
				dayNumber,
				offset
			);
			yield put(programDaysAC.doRequestCalendarProgramDayTodosSuccess(programDays));
		}
	} catch (e) {
		// do nothing atm
	}
}

/**
 * Fetches calendar data for initial load. Needed data are:
 * `dayNumber`: for the redirect from /today to /today/:dayNumber -> containers/views/ProgramFeedRedirectView.js
 * `openItems`: for the navigation -> components/molecules/HeaderNav/additions/countTodos.js
 */
export function* loadCalendarToday() {
	const isAuthenticated = yield select(getIsAuthenticated);
	if (!isAuthenticated) {
		return;
	}

	const isAlreadyLoading = yield select(getSequenceIsRunning, Sequences.CALENDAR_TODAY_IS_LOADING);
	const isOnline = yield select(getIsOnline);

	// Prevent doubled loading calendar data
	if (isAlreadyLoading) {
		return;
	}

	// Prevent call if not online
	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	yield put(sequenceAC.doStartSequence(Sequences.CALENDAR_TODAY_IS_LOADING));

	// Load active program
	try {
		yield* programSagas.loadActiveProgram();
		const activeProgramId = yield select(getActiveProgramId);

		if (activeProgramId !== null) {
			const calendarToday = yield call(ProgramDaysApi.fetchCalendarToday, activeProgramId);
			yield put(programDaysAC.doRequestCalendarTodaySuccess(calendarToday));
			yield put(offlineAvailabilityAC.doRegisterDataBag(DATA_BAG.CALENDAR_TODAY));
		}
	} catch (e) {
		// do nothing atm
	}

	yield put(sequenceAC.doFinishSequence(Sequences.CALENDAR_TODAY_IS_LOADING));
}

/**
 * Wait for `loadCalendarToday` if needed
 * @returns ProgramDayMinType
 */
function* waitForCalendarToday(): IterableIterator<ProgramDayMinType> {
	const todayDay: ProgramDayMinType = (yield select(getCalendarToday) as any) as any;
	if (todayDay) {
		return todayDay;
	}
	yield take(programDaysActionTypes.REQUEST_CALENDAR_TODAY_SUCCESS as any) as any;
	return yield select(getCalendarToday) as any;
}

/**
 * Calendar loads the Calendar Endpoint as well as the active Program
 */
export function* loadCalendar() {
	// is authenticated
	const isAuthenticated = yield select(getIsAuthenticated);
	if (!isAuthenticated) {
		return;
	}

	// is Online
	const isOnline: boolean = yield select(getIsOnline);
	if (!isOnline) {
		return;
	}

	// wait for calendar/today is loaded
	const todayDay: ProgramDayMinType = yield waitForCalendarToday();
	const todayDayNumber: number = todayDay.dayNumber;

	// Prevent doubled loading calendar data
	const isAlreadyLoading: boolean = yield select(getSequenceIsRunning, Sequences.CALENDAR_IS_LOADING);
	if (isAlreadyLoading) {
		return;
	}

	// Do not load again if loaded already, use updateCalendar
	const calendar: Array<ProgramDayType> = yield select(getCalendar);
	if (calendar.length > 0) {
		return;
	}

	yield put(sequenceAC.doStartSequence(Sequences.CALENDAR_IS_LOADING));

	try {
		yield* programSagas.loadActiveProgram();
		const activeProgramId = yield select(getActiveProgramId);

		if (activeProgramId !== null) {
			const calendarForProgram = yield call(ProgramDaysApi.fetchCalendar, activeProgramId);
			yield put(programDaysAC.doRequestCalendarSuccess(calendarForProgram));
			yield put(offlineAvailabilityAC.doRegisterDataBag(DATA_BAG.CALENDAR));

			yield put(sequenceAC.doFinishSequence(Sequences.CALENDAR_IS_LOADING));

			// loadCalendarTodos have to be the last part
			yield loadCalendarTodos(calendarForProgram, activeProgramId, todayDayNumber);
		}
	} catch (e) {
		yield put(sequenceAC.doFinishSequence(Sequences.CALENDAR_IS_LOADING));
	}
}

type UpdateCalendarArgumentType = {
	currentDayNumber: number | undefined;
};

export function* updateCalendar({ currentDayNumber }: UpdateCalendarArgumentType) {
	// Load active program
	yield* programSagas.loadActiveProgram();
	const activeProgramId = yield select(getActiveProgramId);
	let fallbackCurrentDayNumber: number | undefined;

	if (activeProgramId === null) {
		return;
	}

	/**
	 * use given currentDayNumber if exist
	 * @see PULSWM-3084
	 */
	if (typeof currentDayNumber !== 'number') {
		fallbackCurrentDayNumber = yield select(getCurrentDayNumber);
		if (typeof fallbackCurrentDayNumber !== 'number') {
			return;
		}
	}

	const programDays = yield call(
		ProgramDaysApi.fetchCalendarProgramDayTodos as any,
		activeProgramId,
		currentDayNumber || fallbackCurrentDayNumber,
		0
	);
	yield put(programDaysAC.doRequestCalendarProgramDayTodosSuccess(programDays));
}

function* calendarSagas() {
	/* Load Calendar: Initial */
	yield takeEvery(
		[
			loginActionTypes.LOGIN_STATUS_KNOWN,
			programPreviewActionTypes.START_SUCCESS,
			programPreviewActionTypes.OVERWRITE_SUCCESS,
			programItemsActionTypes.REQUEST_COMPLETED_SUCCESS,
			programItemsActionTypes.REQUEST_WORKOUT_COMPLETED_SUCCESS,
			programItemsActionTypes.REQUEST_CLOSE_SUCCESS,
			programItemsActionTypes.REQUEST_SLOGAN_SUCCESS,
			programItemsActionTypes.REQUEST_SAVE_TRACKING_SUCCESS,
		],
		loadCalendarToday
	);

	yield takeEvery([programDaysActionTypes.REQUEST_CALENDAR], loadCalendar);

	/* Update Calendar: Program Start, Change, Check Cards */
	yield takeEvery(
		[
			programPreviewActionTypes.OVERWRITE_SUCCESS,
			programItemsActionTypes.REQUEST_COMPLETED_SUCCESS,
			programItemsActionTypes.REQUEST_WORKOUT_COMPLETED_SUCCESS,
			programItemsActionTypes.REQUEST_CLOSE_SUCCESS,
			programItemsActionTypes.REQUEST_SLOGAN_SUCCESS,
			programItemsActionTypes.REQUEST_SAVE_TRACKING_SUCCESS,
		] as any,
		updateCalendar
	);
}

export default calendarSagas;
